import React, { useEffect, useState, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import useTheme from '@mui/styles/useTheme';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
import { useShipmentsApi } from '../../../apis';
import { dateTimeTzNoZ } from '../../../utils/formatters';
import InlineLoader from '../../../components/InlineLoader';
import DetailItemLabel from './DetailItemLabel';
import EtaRow from './EtaRow';

function EtaItemCard({ name, plant, bol, order, latestUpdate, ...props }) {
	const theme = useTheme();
	const { t } = useTranslation('order_details');
	const [isLoading, setIsLoading] = useState(false);
	const [status, setStatus] = useState();
	const isMountedRef = useIsMountedRef();
	const [hasError, setHasError] = useState(false);
	const [details, setDetails] = useState();
	const [shipmentStops, setShipmentStops] = useState();
	const [latestStopStatuses, setLatestStopStatuses] = useState();
	const { getOrderShipmentVisibility } = useShipmentsApi();

	const fetchTrackingDetails = useCallback(async () => {
		setIsLoading(true);
		setHasError(false);
		try {
			if (isMountedRef.current) {
				const response = await getOrderShipmentVisibility({
					OrderNumber: order,
					BOLNumber: bol,
				});

				if (response?.isError || response?.isAborted || !response?.data) {
					throw (
						response?.error || new Error('Shipment visibility request failed')
					);
				}

				const parsedData = response.data;
				setDetails(parsedData);
				setStatus(parsedData.MPRequestDocument.statusUpdates);
				setShipmentStops(parsedData.MPRequestDocument.shipment.shipmentStops);
				setLatestStopStatuses(parsedData.MPRequestDocument.latestStopStatuses);
			}
		} catch (err) {
			console.log('err', err);
			setHasError(true);
			setDetails();
		}
		setIsLoading(false);
	}, [isMountedRef, getOrderShipmentVisibility, bol, order]);

	useEffect(() => {
		fetchTrackingDetails();
	}, [fetchTrackingDetails]);

	const merge = (arr1, arr2) => {
		const temp = [];

		arr1.forEach((x) => {
			arr2.forEach((y) => {
				if (x.stopNumber === y.stopNumber) {
					temp.push({ ...x, ...y });
				}
			});
		});

		return temp;
	};

	// sort status by timestamp for shipment status
	const sortStatus = () =>
		status &&
		status
			.sort((x, y) => (new Date(x.timestamp) < new Date(y.timestamp) ? 1 : -1))
			.reverse();

	const latestStatus =
		status &&
		sortStatus()
			.map((item) => item.statusCode)
			.slice(-1)
			.toString();

	const allStops = details ? merge(shipmentStops, latestStopStatuses) : '';
	const stops = details ? [allStops?.shift(), allStops?.pop()] : '';

	const arrival = () => {
		let time = '';
		if (!stops?.length) {
			return '';
		}
		for (let i = 0; i < stops?.length; i += 1) {
			time = stops[i].arrivalDateTime;
		}
		return time;
	};

	const destination = () => {
		let delivery = '';

		if (!stops?.length) {
			return '';
		}

		for (let i = 0; i < stops?.length; i += 1) {
			delivery = stops[i].stopName === 'Destination' ? stops[i].stopName : '';
		}
		return delivery;
	};

	let div;
	if (isLoading) {
		div = (
			<InlineLoader
				ariaLabel={t('loading')}
				sx={{
					color: theme.palette.trackingDrawer.bol,
					mt: 1,
				}}
			/>
		);
	} else if (hasError) {
		div = (
			<Typography
				variant="caption1"
				tabIndex={0}
				role="paragraph"
				aria-label={t('tracking_detail_not_available')}
			>
				{t('tracking_detail_not_available')}
			</Typography>
		); // todo: improve this experience
	}

	return (
		<Grid item xs={12} {...props} aria-label={t('eta')}>
			<DetailItemLabel label={name} />

			<Grid item xs={6} role="paragraph" aria-label={name}>
				{div || (
					<EtaRow
						title={
							(details?.ETAtimestampUTC &&
								latestStatus?.includes('COMPLETED')) ||
							(details?.ETAtimestampUTC &&
								arrival() !== undefined &&
								destination() === 'Destination')
								? ''
								: details &&
								  details?.ETAtimestampUTC !== null && (
										<Typography
											variant="body2"
											color={theme.palette.trackingDrawer.bol}
											role="paragraph"
											aria-label={dateTimeTzNoZ(details?.ETAtimestampUTC)}
										>
											{dateTimeTzNoZ(details?.ETAtimestampUTC)}
										</Typography>
								  )
						}
						style={{
							display: 'block',
						}}
					/>
				)}
			</Grid>
		</Grid>
	);
}

EtaItemCard.propTypes = {
	name: PropTypes.string.isRequired,
	plant: PropTypes.string.isRequired,
	bol: PropTypes.string.isRequired,
	order: PropTypes.string.isRequired,
	latestUpdate: PropTypes.object,
};

export default EtaItemCard;
